import React, { useEffect, useState, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { FormikProvider, useFormik } from 'formik';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { stepperInfo } from '../stepperInfo';
import { ButtonsPanel } from '../ButtonsPanel/ButtonsPanel';
import { StepThreeFormFields } from './StepThreeFormFields';
import { renderConfirmFlightInfoModal } from '../../../../components';
import { DateHelper, transformDraftToRide, LodashHelper } from '../../../../utils';
import { useCheckForMatchedTc } from '../../../../hooks/useCheckForMatchedTc';
import { useUberHealthServiceAvailability } from '../../../../hooks/useUberHealthServiceAvailability';
import {
  ADDITIONAL_DELAY_FOR_RIDE_CREATION,
  MINIMUM_DELAY_FOR_ROUNDTRIP_RIDE,
  URLS,
} from '../../../../constants/constants';

import styles from './index.module.css';
import { useLocation } from 'react-router-dom';
import { defaultTimeValues } from '../../Preferences/MHPreferences/partials/defaultValues';

export const StepThree = ({
  step,
  draft,
  handleSaveAndClose,
  handleSubmit,
  handlePreviousStep,
  defaultTimeZone,
  platformPreferences,
  setFormikValues,
  showSubInformation = true,
  disabledValues,
  airportBufferTime,
  validateReturnTime = true,
  quickSchdule = false,
  setStepValid
}) => {


  const [visibleFlightInfoModal, setFlightInfoModalVisible] = useState(false);
  const [updateStepthreeDraft, setUpdatedStepthreeDraft] = useState(draft?.step3);

  const location = useLocation();
  const matchedErrorRef = useRef();
  const hasMatchedTC = useSelector((state) => state.rides.hasMatchedTC);
  const uberHealthRouteAvailable = useSelector((state) => state.rides.uberHealthRouteAvailable);
  const uberHealthRouteCheckPending = useSelector((state) => state.rides.uberHealthRouteCheckPending);

  const windowOfTimeIsValid = useSelector((state) => state.rides.windowOfTimeIsValid);


  useEffect(() => {
    if (draft?.step3?.flightInfo && draft?.step3?.airportBufferTime && draft?.step3?.pickUpDate?.datetime) {
      const subtractedDate = DateHelper?.subtractMinutesInDateWithoutFormat(draft?.step3?.pickUpDate?.datetime, draft?.step3?.airportBufferTime);
      setUpdatedStepthreeDraft({ ...draft?.step3, pickUpDate: { ...draft?.step3?.pickUpDate, datetime: subtractedDate } });
    }

  }, [draft?.step3?.flightInfo, draft?.step3?.airportBufferTime, draft?.step3?.pickUpDate?.datetime, draft?.step3]);

  const thirdStepInitialValues = useMemo(() => {


    const initialValuesWithDefaultTZ = {
      ...stepperInfo[2].values,
      ...draft?.step3,
      pickUpDate: draft?.step3?.pickUpDate || {
        timezone: defaultTimeZone,
      },
    };

    if (draft?.step3?.isRecurring && draft?.step3?.isRoundtrip) {
      return {
        ...initialValuesWithDefaultTZ,
        returnTime: draft?.step3?.returnTime || {
          timezone: defaultTimeZone,
        },
        recurrenceRule: {
          ...initialValuesWithDefaultTZ?.recurrenceRule,
          startPoint: draft?.step3?.recurrenceRule?.startPoint ||
            draft?.step3?.pickUpDate || {
            timezone: defaultTimeZone,
          },
          returnStartPoint: draft?.step3?.recurrenceRule?.returnStartPoint ||
            draft?.step3?.returnTime || {
            timezone: defaultTimeZone,
          },
        },
      };
    } else if (draft?.step3?.isRecurring) {
      return {
        ...initialValuesWithDefaultTZ,
        recurrenceRule: {
          ...initialValuesWithDefaultTZ?.recurrenceRule,
          startPoint: draft?.step3?.recurrenceRule?.startPoint ||
            draft?.step3?.pickUpDate || {
            datetime: draft?.step3?.pickUpDate?.datetime || '',
            timezone: defaultTimeZone,
          },
        },
      };
    } else if (draft?.step3?.isRoundtrip) {
      return {
        ...initialValuesWithDefaultTZ,
        returnTime: draft?.step3?.returnTime || {
          timezone: defaultTimeZone,
        },
      };
    } else {
      return initialValuesWithDefaultTZ;
    }
  }, [draft, defaultTimeZone]);


  const isChangedFlightInfo = () => {
    return values?.flightInfo && !LodashHelper.isEqual(values?.flightInfo, thirdStepInitialValues?.flightInfo);
  }

  const handleSubmitWithConfirm = (currentValues, stepNumber) => {
    if (currentValues?.flightInfo && currentValues?.pickUpDate && airportBufferTime) {
      const updatePickupDate = DateHelper.addMinutesInDateWithoutFormat(currentValues?.pickUpDate?.datetime, airportBufferTime);
      Object.assign(currentValues, { pickUpDate: { ...currentValues?.pickUpDate, datetime: updatePickupDate }, airportBufferTime: airportBufferTime });
    }
    if (isChangedFlightInfo()) {
      setFlightInfoModalVisible(true);
    }
    else
      handleSubmit(currentValues, stepNumber);
  }

  const formik = useFormik({
    enableReinitialize: true,
    validateOnMount: true,
    validateOnChange: true,
    validateOnBlur: true,
    initialValues: thirdStepInitialValues,
    validationSchema: stepperInfo[2].schema(validateReturnTime),
    onSubmit: handleSubmitWithConfirm,
  });

  const { values, touched, errors, validateForm, setFieldTouched, setFieldValue, isValid } = formik;

  useEffect(() => {
    setFormikValues && setFormikValues(values, isValid, 'step3');
    return;
  }, [values, isValid, setFormikValues]);

  useEffect(() => {
    !hasMatchedTC && matchedErrorRef?.current?.scrollIntoView({ behavior: 'smooth' });
    return;
  }, [hasMatchedTC]);

  const currentVehicleType = useMemo(() => draft?.step2?.vehicleType, [draft]);
  const isUberRide = useMemo(() => currentVehicleType === 'uberAmbulatoryRide', [currentVehicleType]);

  const valuesForMatching = useMemo(() => {
    const inWizardStep3 =
      (Number(step) === 2 && location.pathname === URLS.CREATE_RIDE) ||
      location.pathname === URLS.EDIT_DRAFT;
    const inQuickSchedule = Boolean(quickSchdule);
    if (!inWizardStep3 && !inQuickSchedule) {
      return null;
    }
    return transformDraftToRide({
      ...draft,
      step3: values,
    });
  }, [draft, step, values, location, quickSchdule]);

  useEffect(() => {
    if (!setStepValid) {
      return;
    }
    if (quickSchdule && isUberRide) {
      setStepValid(
        formik.isValid &&
          uberHealthRouteAvailable &&
          !uberHealthRouteCheckPending
      );
      return;
    }
    setStepValid(formik.isValid);
  }, [
    formik.isValid,
    setStepValid,
    quickSchdule,
    isUberRide,
    uberHealthRouteAvailable,
    uberHealthRouteCheckPending,
  ]);

  // Skip TC matching for Uber rides - they're automatically assigned to Uber Health
  useCheckForMatchedTc(valuesForMatching, Boolean(valuesForMatching) && !isUberRide);
  useUberHealthServiceAvailability(valuesForMatching, Boolean(valuesForMatching) && isUberRide);

  useEffect(() => {
    if (isUberRide && !uberHealthRouteAvailable && !uberHealthRouteCheckPending) {
      matchedErrorRef?.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [isUberRide, uberHealthRouteAvailable, uberHealthRouteCheckPending]);

  return (
    <>
      {!quickSchdule && <h2
        className={classNames({
          [styles.headTitle]: showSubInformation,
          [styles.editingHeadTitle]: !showSubInformation,
        })}
        ref={matchedErrorRef}>
        {showSubInformation && `Step 3:`} Location and Time
      </h2>}

      <FormikProvider value={formik}>
        <form onSubmit={formik?.handleSubmit} className={styles.form}>

          <StepThreeFormFields
            quickSchdule={quickSchdule}
            isUberRide={isUberRide}
            hasMatchedTC={hasMatchedTC}
            uberHealthRouteAvailable={uberHealthRouteAvailable}
            uberHealthRouteCheckPending={uberHealthRouteCheckPending}
            defaultValues={updateStepthreeDraft}
            defaultTimeZone={defaultTimeZone}
            minimumRoundTrip={platformPreferences?.minimumRoundTrip || MINIMUM_DELAY_FOR_ROUNDTRIP_RIDE}
            defaultHomeAddress={draft?.step1?.riderInfo?.homeAddress}
            minDelayToStartRide={
              isUberRide
                ? { hours: 0, minutes: 30 }
                : platformPreferences?.minTimeFromRideRequestToPickup[currentVehicleType] &&
                DateHelper.addHours(
                  platformPreferences?.minTimeFromRideRequestToPickup[currentVehicleType],
                  ADDITIONAL_DELAY_FOR_RIDE_CREATION,
                  draft?.step2?.needsCarSeat ? platformPreferences?.carBoosterSeatBufferTime : defaultTimeValues
                )
            }
            maxPickUpWindowOfTime={platformPreferences?.maxPickUpWindowOfTime}
            vehicleType={currentVehicleType}
            values={values}
            touched={touched}
            errors={errors}
            validateForm={validateForm}
            setFieldTouched={setFieldTouched}
            setFieldValue={setFieldValue}
            disabledValues={disabledValues}
            windowOfTimeIsValid={windowOfTimeIsValid}
            airportBufferTime={airportBufferTime}


          />
          {visibleFlightInfoModal &&
            renderConfirmFlightInfoModal({
              handleConfirm: () => handleSubmit(formik.values, step),
              handleCancel: () => setFlightInfoModalVisible(false)
            })}
          {showSubInformation && (
            <ButtonsPanel
              disabled={
                !isValid ||
                (!hasMatchedTC && !isUberRide) ||
                !windowOfTimeIsValid ||
                (isUberRide &&
                  (uberHealthRouteCheckPending || !uberHealthRouteAvailable))
              }
              step={step}
              handlePreviousStep={handlePreviousStep}
              handleSaveAndClose={handleSaveAndClose}
              handleSubmit={handleSubmit}
            />
          )}
        </form>
      </FormikProvider>
    </>
  );
};

StepThree.propTypes = {
  step: PropTypes.number,
  draft: PropTypes.object,
  handleSaveAndClose: PropTypes.func,
  handleSubmit: PropTypes.func,
  handlePreviousStep: PropTypes.func,
  platformPreferences: PropTypes.object,
  airportBufferTime: PropTypes.oneOfType([PropTypes.number, PropTypes.any]),
};
